import { useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { isGerman } from '@/lib/language';

export function useLanguage() {
  const { i18n } = useTranslation();
  const { user } = useAuth();

  /**
   * Sync i18n language from Supabase profile on auth.
   *
   * Der Lauf ist ABBRECHBAR, und das ist der Punkt: beim Kontowechsel baut
   * React diesen Effekt ab, waehrend die Profilabfrage des VORIGEN Nutzers
   * noch laeuft. Ohne `abgemeldet` liefe deren Antwort danach ein und setzte
   * die Sprache von Nutzer A fuer Nutzer B -- eine Wettlaufsituation, die sich
   * im Betrieb als "die App startet in der falschen Sprache" zeigt und sich
   * nicht reproduzieren laesst, weil sie an der Antwortzeit haengt.
   *
   * `await i18n.changeLanguage(...)` statt `.then()`: der Wechsel ist selbst
   * asynchron, und erst danach traegt `i18n.language` den neuen Wert.
   */
  useEffect(() => {
    if (!user?.id) return;
    let abgemeldet = false;
    void (async () => {
      try {
        const { data } = await supabase
          .from('profiles')
          .select('language')
          .eq('user_id', user.id)
          .single();
        if (abgemeldet) return;
        if (data?.language && data.language !== i18n.language) {
          await i18n.changeLanguage(data.language);
        }
      } catch {
        // Eine gescheiterte Auskunft laesst die Sprache stehen, die der
        // Detector gewaehlt hat. Mehr erfahren wir ueber sie nicht.
      }
    })();
    return () => {
      abgemeldet = true;
    };
  }, [user?.id, i18n]);

  // Check for ?lang= URL param (cross-domain handoff from marketing site)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const langParam = params.get('lang');
    if (langParam && ['de', 'en'].includes(langParam)) {
      i18n.changeLanguage(langParam);
      localStorage.setItem('feed-optimizer-lang', langParam);
      params.delete('lang');
      const newUrl = params.toString()
        ? `${window.location.pathname}?${params}`
        : window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, [i18n]);

  const setLanguage = useCallback(async (lang: 'de' | 'en') => {
    i18n.changeLanguage(lang);
    localStorage.setItem('feed-optimizer-lang', lang);

    if (user?.id) {
      await Promise.all([
        supabase
          .from('profiles')
          .update({ language: lang } as any)
          .eq('user_id', user.id),
        supabase
          .from('email_preferences' as any)
          .update({ language: lang })
          .eq('user_id', user.id),
      ]);
    }
  }, [user?.id, i18n]);

  return {
    language: (isGerman(i18n.language) ? 'de' : 'en') as 'de' | 'en',
    setLanguage,
  };
}
